import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKingdomDossier } from '../api/dossier';
import type { KingdomDossierDto } from '../types/dossier';

interface UseDossierResult {
  dossier: KingdomDossierDto | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetches the Kingdom Dossier for the given target kingdom.
 *
 * Refetches automatically when kingdomId changes.
 */
export function useDossier(kingdomId: string | null): UseDossierResult {
  const { worldId } = useAuth();
  const [dossier, setDossier] = useState<KingdomDossierDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // SF-156: tracks the kingdomId the effect below last ran for, so a refresh() (refreshKey bump
  // with the same kingdomId) is distinguishable from a real kingdom-to-kingdom navigation — only
  // the latter should clear `dossier`. A background refresh must never blank existing content
  // (SF-151 non-exclusive-render-state precedent).
  const lastKingdomIdRef = useRef<string | null>(null);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!kingdomId || !worldId) {
      setDossier(null);
      lastKingdomIdRef.current = kingdomId;
      setIsLoading(false);
      return;
    }

    // SF-156 fix: clear the previous kingdom's payload whenever kingdomId *changes* to a
    // different non-null value (not just when it becomes falsy) — symmetric with the branch
    // above. Without this, a kingdom-to-kingdom navigation left `dossier` holding the prior
    // kingdom's data for the entire in-flight fetch window, letting page-level derived state
    // (e.g. `?member=` resolution) transiently resolve against the wrong kingdom's roster.
    if (lastKingdomIdRef.current !== kingdomId) {
      setDossier(null);
    }
    lastKingdomIdRef.current = kingdomId;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getKingdomDossier(worldId, kingdomId)
      .then((data) => {
        if (!cancelled) {
          setDossier(data);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load dossier');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [kingdomId, worldId, refreshKey]);

  return { dossier, isLoading, error, refresh };
}
